import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isBrowser } from "react-device-detect";
import { Link } from "react-router-dom";

import { MobileHomeDashboard, QuickActions } from "..";
import {
  ColumnOneTableCellSkeleton,
  PrimaryButton,
  StepType,
  Table,
  TableCellSkeleton,
} from "../../components";
import { SharedSessionColumns } from "../../components/shared/SharedSessionColumns";
import { useModalContext } from "../../contexts/ModalContext";
import { usePlayerContext } from "../../contexts/PlayerContext";
import { useUIContext } from "../../contexts/UIContext";
import { PlayerForm } from "../../features/Player/PlayerForm";
import { usePlayerLoading } from "../../hooks/usePlayerLoading";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { orgSlice } from "../../redux/slices/orgSlice";
import { playerSlice } from "../../redux/slices/playerSlice";
import {
  useGetOrgInfoQuery,
  useGetOrgMovementTypesAndTagsQuery,
  useGetPlayerByIdQuery,
  useGetSessionsByStatusQuery,
  useGetSessionTypesQuery,
} from "../../services";
import {
  emptyPlayerFormValues,
  hasDashboardCriticalError,
  normalizeFetchError,
  uploadSessionStepList,
} from "../../shared/Constants";
import {
  enableNextButtonInModal,
  parseCompletedSessions,
  parseInProgressSessions,
  upsertPlayerById,
} from "../../shared/Functions";
import { ApiMovementTypesAndTags, MovementType } from "../../types";

export const HomeDashboard: React.FC = (): JSX.Element => {
  // State and AppContext
  const [selectedPageSize, setSelectedPageSize] = useState(
    sessionStorage.getItem("homeDashboardPageSize") || "10",
  );
  const [isRetrying, setIsRetrying] = useState(false);
  const retryFetchStartedRef = useRef(false);
  const { setModalState, modalState } = useModalContext();
  const { playerIdToRetrieve, setPlayerIdToRetrieve } = usePlayerContext();
  const { setDisplayBack, setLoadingDashboard, setDashboardError } =
    useUIContext();

  // Redux calls
  const uploadSessionFormValues = useAppSelector(
    (state) => state.uploadSessionForm,
  );
  const orgData = useAppSelector((state) => state.org);
  const pendingSessionCount = orgData.sessions.pending;

  // Loads players in parallel; provides loading state for dashboard skeleton
  const { storedPlayerList, fetchingPlayers, playersLoadComplete } =
    usePlayerLoading();

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { storePlayers } = playerSlice.actions;
  const { setOrgInformation, updateOrgInformation } = orgSlice.actions;

  // RTK Queries
  const {
    data: org,
    isFetching: fetchingOrgInfo,
    isError: orgInfoError,
    error: orgError,
    refetch: refetchOrg,
  } = useGetOrgInfoQuery(undefined);
  const {
    data: orgMovements,
    isFetching: fetchingOrgMovements,
    refetch: refetchOrgMovements,
  } = useGetOrgMovementTypesAndTagsQuery(orgData.id, {
    skip:
      orgData.details.movementTypes && orgData.details.movementTypes.length > 0,
  });
  const {
    data: sessionTypes,
    isFetching: fetchingSessionTypes,
    refetch: refetchSessionTypes,
  } = useGetSessionTypesQuery(undefined, {
    skip:
      orgData.details.sessionTypes && orgData.details.sessionTypes.length > 0,
  });
  const {
    data: pendingSessions,
    isFetching: fetchingPending,
    refetch: refetchPendingSessions,
  } = useGetSessionsByStatusQuery(
    {
      status: "pending",
      limit: isBrowser ? 3 : pendingSessionCount,
      offset: 0,
    },
    { skip: pendingSessionCount === 0 },
  );
  const {
    data: processedSessions,
    isFetching: fetchingProcessed,
    isError: processedSessionsError,
    refetch: refetchProcessedSessions,
  } = useGetSessionsByStatusQuery({
    status: "processed",
    limit: 7,
    offset: 0,
  });
  const { data: singlePlayer } = useGetPlayerByIdQuery(playerIdToRetrieve, {
    skip: !playerIdToRetrieve,
  });

  // Comprehensive loading state: show skeleton until ALL required data is loaded
  const isLoadingDashboard = useMemo(() => {
    // Always required queries
    const requiredQueriesLoading =
      fetchingOrgInfo || fetchingProcessed || fetchingPlayers;

    // Conditionally required queries (only check if not skipped)
    const needsOrgMovements =
      !orgData.details.movementTypes ||
      orgData.details.movementTypes.length === 0;
    const needsSessionTypes =
      !orgData.details.sessionTypes ||
      orgData.details.sessionTypes.length === 0;
    const needsPendingSessions = pendingSessionCount > 0;

    const conditionalQueriesLoading =
      (needsOrgMovements && fetchingOrgMovements) ||
      (needsSessionTypes && fetchingSessionTypes) ||
      (needsPendingSessions && fetchingPending);

    // Keep loading if org info is loaded, we have players to fetch, but queries haven't started yet
    // This prevents flicker when org query finishes but fetchingPlayers hasn't updated yet
    const orgInfoLoaded = !fetchingOrgInfo && orgData.id !== "";
    // Keep loading if org query finished but Redux hasn't updated orgData yet
    const waitingForOrgDataUpdate = !fetchingOrgInfo && orgData.id === "";
    const needsToFetchPlayers =
      orgData.players.total > 0 &&
      storedPlayerList.length < orgData.players.total;
    // Only "waiting" if we haven't completed the load. When playersLoadComplete
    // (API sent all data), stop waiting even if stored < org count (dedup, etc).
    const waitingForPlayerQueries =
      orgInfoLoaded &&
      needsToFetchPlayers &&
      !fetchingPlayers &&
      !playersLoadComplete;

    const result =
      requiredQueriesLoading ||
      conditionalQueriesLoading ||
      fetchingOrgInfo ||
      waitingForOrgDataUpdate ||
      waitingForPlayerQueries;

    return result;
  }, [
    fetchingOrgInfo,
    fetchingProcessed,
    fetchingPlayers,
    playersLoadComplete,
    fetchingOrgMovements,
    fetchingSessionTypes,
    fetchingPending,
    orgData.details.movementTypes,
    orgData.details.sessionTypes,
    orgData.id,
    orgData.players.total,
    pendingSessionCount,
    storedPlayerList.length,
  ]);

  const needsOrgMovements = !orgData.details.movementTypes?.length;
  const needsSessionTypes = !orgData.details.sessionTypes?.length;
  const hasCriticalError = hasDashboardCriticalError(orgInfoError, orgError);
  const showErrorUi = hasCriticalError && !isRetrying;
  const showDashboardLoading = isLoadingDashboard || isRetrying;

  const handleRetry = useCallback(() => {
    retryFetchStartedRef.current = false;
    setIsRetrying(true);
    void refetchOrg();
    void refetchProcessedSessions();
    if (needsOrgMovements && orgData.id) {
      void refetchOrgMovements();
    }
    if (needsSessionTypes) {
      void refetchSessionTypes();
    }
    if (pendingSessionCount > 0) {
      void refetchPendingSessions();
    }
  }, [
    refetchOrg,
    refetchOrgMovements,
    refetchSessionTypes,
    refetchPendingSessions,
    refetchProcessedSessions,
    needsOrgMovements,
    needsSessionTypes,
    orgData.id,
    pendingSessionCount,
  ]);

  useEffect(() => {
    if (!isRetrying) return;

    const criticalRefetching =
      fetchingOrgInfo ||
      fetchingProcessed ||
      (needsSessionTypes && fetchingSessionTypes);

    if (criticalRefetching) {
      retryFetchStartedRef.current = true;
      return;
    }

    if (retryFetchStartedRef.current) {
      retryFetchStartedRef.current = false;
      setIsRetrying(false);
    }
  }, [
    isRetrying,
    fetchingOrgInfo,
    fetchingProcessed,
    fetchingSessionTypes,
    needsSessionTypes,
  ]);

  useEffect(() => {
    setLoadingDashboard(showErrorUi ? false : showDashboardLoading);
  }, [showErrorUi, showDashboardLoading, setLoadingDashboard]);

  useEffect(() => {
    setDashboardError(showErrorUi);
    return () => setDashboardError(false);
  }, [showErrorUi, setDashboardError]);

  const orgUpdates = useMemo(() => {
    if (!org) return null;

    if (orgData.id === "") {
      return { type: "setOrgInformation", payload: org };
    } else if (org.players !== orgData.players) {
      return {
        type: "updateOrgInformation",
        payload: { players: org.players },
      };
    } else if (org.sessions !== orgData.sessions) {
      return {
        type: "updateOrgInformation",
        payload: { sessions: org.sessions },
      };
    }
    return null;
  }, [org, orgData]);

  useEffect(() => {
    if (orgUpdates) {
      if (orgUpdates.type === "setOrgInformation") {
        dispatch(setOrgInformation(orgUpdates.payload));
      } else if (orgUpdates.type === "updateOrgInformation") {
        dispatch(updateOrgInformation(orgUpdates.payload));
      }
    }
  }, [orgUpdates, dispatch, setOrgInformation, updateOrgInformation]);

  const processedData = useMemo(() => {
    if (sessionTypes) {
      const sortedSessionTypes = [...sessionTypes].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      return { type: "sessionTypes", data: sortedSessionTypes };
    } else if (orgMovements) {
      const movementTagsAndTypes = orgMovements.map(
        (item: ApiMovementTypesAndTags): MovementType => {
          return {
            id: item.movementType.id,
            name: item.movementType.name,
            slug: item.movementType.slug,
            tags: item.movementTags,
          };
        },
      );
      return { type: "movementTypes", data: movementTagsAndTypes };
    }
    return null;
  }, [sessionTypes, orgMovements]);

  useEffect(() => {
    if (processedData) {
      if (processedData.type === "sessionTypes") {
        dispatch(updateOrgInformation({ sessionTypes: processedData.data }));
      } else if (processedData.type === "movementTypes") {
        dispatch(updateOrgInformation({ movementTypes: processedData.data }));
      }
    }
  }, [processedData, dispatch, updateOrgInformation]);

  // Data processing and parsing
  const inProgressSessionList = parseInProgressSessions(pendingSessions || []);
  const completedSessionList = parseCompletedSessions(processedSessions || []);
  const memoizedSharedSessionColumns = useMemo(SharedSessionColumns, []);

  const completedTableData = useMemo(
    () => completedSessionList,
    [completedSessionList],
  );

  const completedTableColumns = useMemo(
    () => memoizedSharedSessionColumns,
    [memoizedSharedSessionColumns],
  );

  const skeletonTableColumns = useMemo(
    () =>
      memoizedSharedSessionColumns.map((column, index) => ({
        ...column,
        header: <TableCellSkeleton />,
        cell:
          index === 0 ? <ColumnOneTableCellSkeleton /> : <TableCellSkeleton />,
        enableSorting: false,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (singlePlayer && storedPlayerList) {
      const updatedPlayerList = upsertPlayerById(
        storedPlayerList,
        singlePlayer,
      );
      dispatch(storePlayers(updatedPlayerList));
      setPlayerIdToRetrieve("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singlePlayer]);

  const stepList: StepType[] = useMemo((): StepType[] => {
    if (orgData && uploadSessionFormValues) {
      return uploadSessionStepList({
        playerList: storedPlayerList,
        sessionFormValues: uploadSessionFormValues,
        orgData: orgData,
      });
    } else {
      return [];
    }
  }, [orgData, uploadSessionFormValues, storedPlayerList]);

  const openModal = (modalType: string, title?: string) => {
    if (modalType === "upload") {
      setModalState((prev) => ({
        ...prev,
        shouldOpen: true,
        title: "Upload Data",
        steps: stepList,
      }));
    } else if (modalType === "addPlayer" && title) {
      setModalState((prev) => ({
        ...prev,
        shouldOpen: true,
        title: title,
        singleStep: <PlayerForm formValues={emptyPlayerFormValues} />,
      }));
    }
  };

  const checkIfNextButtonIsEnabled = useCallback(() => {
    enableNextButtonInModal(modalState, setModalState, stepList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.steps, stepList]);

  useEffect(() => {
    setDisplayBack && setDisplayBack(false);
    checkIfNextButtonIsEnabled();
  }, [setDisplayBack, checkIfNextButtonIsEnabled]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      className={`relative z-0 overflow-y-auto w-full ${showErrorUi ? "flex flex-1 min-h-0" : "pb-20"}`}
    >
      {showErrorUi ? (
        <div
          className="flex-1 min-h-[calc(100vh-4rem)] bg-gray-50 flex flex-col justify-center items-center px-8 w-full"
          data-testid="home-dashboard-error"
        >
          <div className="flex flex-col items-center text-center max-w-md">
            <h2 className="font-bold text-2xl text-gray-900">
              We&apos;re having trouble loading the dashboard
            </h2>
            <p className="pt-5 text-gray-700 leading-relaxed text-base font-light mb-4">
              The service may be temporarily unavailable. Please try again.
            </p>
            <PrimaryButton
              onClick={handleRetry}
              data-testid="home-dashboard-retry-button"
              aria-label="Retry loading dashboard"
            >
              Try Again
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <>
          <div
            className="hidden lg:block mt-8 px-4"
            data-testid="desktop-home-dashboard-container"
          >
            <QuickActions
              loadingDashboard={showDashboardLoading}
              openModal={() => openModal("upload")}
            />
            <div data-testid="recently-completed-sessions-container">
              <div className="max-w-8xl mx-auto justify-between items-center pb-2 pt-8">
                {showDashboardLoading ? (
                  <div
                    className="mt-4 mx-4 h-12 w-96 rounded-lg bg-gray-200 animate-pulse sm:mx-6 lg:mx-8 lg:mb-6"
                    data-testid="skeleton-header"
                  ></div>
                ) : (
                  <div className="flex justify-start items-center">
                    <h2
                      className="mt-4 px-4 text-3xl leading-6 font-bold text-gray-900 sm:px-6 lg:px-8 lg:pb-6"
                      data-testid="recently-completed-sessions-header"
                    >
                      Recently Completed Sessions
                    </h2>
                    <Link
                      to="/completed-sessions"
                      className="text-blue-800 hover:text-blue-600"
                      data-testid="recently-completed-sessions-view-all-link"
                    >
                      View All
                    </Link>
                  </div>
                )}
              </div>
              {showDashboardLoading ? (
                <div data-testid="skeleton-table">
                  <Table
                    columns={skeletonTableColumns}
                    data={Array(7).fill({})}
                    selectedPageSize={selectedPageSize}
                    displayFooter={false}
                    fetching={true}
                    hideSearch={true}
                  />
                </div>
              ) : (
                <div data-testid="recently-completed-sessions-table-container">
                  <Table
                    columns={completedTableColumns}
                    data={completedTableData}
                    data-testid="recently-completed-sessions-table"
                    selectedPageSize={selectedPageSize}
                    setSelectedPageSize={setSelectedPageSize}
                    displayFooter={false}
                    fetching={fetchingProcessed}
                    fetchError={
                      processedSessionsError ? normalizeFetchError() : null
                    }
                    customRowClassName="recently-completed-sessions-row"
                    hideSearch={true}
                  />
                </div>
              )}
            </div>
          </div>

          <div
            className="lg:hidden"
            data-testid="mobile-home-dashboard-container"
          >
            <MobileHomeDashboard
              isLoadingDashboard={showDashboardLoading}
              inProgressSessionList={inProgressSessionList}
              openModal={() => openModal("upload")}
            />
          </div>
        </>
      )}
    </main>
  );
};
